import { CompanyType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { useAdminOrganizationDetailQuery } from '@queries/admin-organizations.query';
import { useAdminGetTags } from '@queries/admin-users.query';
import { safeFormatDateTime } from '@utils/date.util';
import { RotateCcwIcon, TrashIcon } from 'lucide-react';
import React, { Suspense, useMemo, useState } from 'react';
import { useOrganizationActions } from '../hooks/use-organization-actions.hook';
import { AdminOrganizationDetailsTile } from './admin-organization-details-tile.component';
import { AdminOrganizationFeatureFlagsTile } from './admin-organization-feature-flags-tile.component';
import { AdminOrganizationHierarchyTile } from './admin-organization-hierarchy-tile.component';
import { AdminOrganizationProfileTile } from './admin-organization-profile-tile.component';
import { AdminOrganizationTagsTile } from './admin-organization-tags-tile.component';
import { AdminOrganizationUsersTile } from './admin-organization-users-tile.component';

type AdminOrganizationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: string;
};

type AdminOrganizationsModalContentProps = {
  organizationId: string;
};

const AdminOrganizationsModalContent: React.FC<AdminOrganizationsModalContentProps> = (props) => {
  const { organizationId } = props;

  const { data: organization } = useAdminOrganizationDetailQuery(organizationId);
  const { data: availableTags = [] } = useAdminGetTags({ onlyUsedByCompanies: true });
  const { handleRestore, isBusy: isActionBusy } = useOrganizationActions({ organizationId });

  const availableTagNames = useMemo(() => availableTags.map((tag) => tag.name), [availableTags]);
  const isDeleted = organization.deleted;
  // AZ-451: Access Controls (company tags) and Organization Hierarchy are innovator/expert constructs
  // with no consumer in MEIA/study/intel code paths, so they are hidden for Intelligence Organizations.
  const isIntelligenceOrg = organization.type === CompanyType.INTELLIGENCE;

  return (
    <div className="flex flex-col gap-4">
      {isDeleted && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <TrashIcon className="size-4 text-red-600" />
            <span className="text-sm text-red-700 font-medium">This organization is deleted</span>
            <span className="text-xs text-red-600">Deleted on {safeFormatDateTime(organization.deletedAt)}</span>
          </div>

          <IconButton
            ariaLabel="Restore organization"
            disabled={isActionBusy}
            icon={<RotateCcwIcon className="size-4" />}
            onClick={handleRestore}
            size="sm"
            variant="secondary"
          />
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          <AdminOrganizationDetailsTile
            isDeleted={Boolean(isDeleted)}
            organization={organization}
            organizationId={organizationId}
          />
          {!isIntelligenceOrg && (
            <AdminOrganizationTagsTile
              availableTagNames={availableTagNames}
              isDeleted={Boolean(isDeleted)}
              organization={organization}
              organizationId={organizationId}
            />
          )}
          <AdminOrganizationProfileTile organization={organization} />
        </div>

        <div className="flex flex-col gap-4 min-h-0 flex-1">
          <AdminOrganizationUsersTile organization={organization} />
          {!isIntelligenceOrg && (
            <AdminOrganizationHierarchyTile
              isDeleted={Boolean(isDeleted)}
              organization={organization}
              organizationId={organizationId}
            />
          )}
          <AdminOrganizationFeatureFlagsTile
            isDeleted={Boolean(isDeleted)}
            organization={organization}
            organizationId={organizationId}
          />
        </div>
      </section>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
};

const AdminOrganizationsModalHeaderTitle: React.FC<{ organizationId: string }> = (props) => {
  const { organizationId } = props;
  const { data: organization } = useAdminOrganizationDetailQuery(organizationId);

  return <span>Organization: {organization.name}</span>;
};

const AdminOrganizationsModalFooterWrapper: React.FC<{ onClose: () => void; organizationId: string }> = (props) => {
  const { onClose, organizationId } = props;

  const { data: organization } = useAdminOrganizationDetailQuery(organizationId);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { handleDelete, handleRestore, isBusy, isDeleting, isRestoring } = useOrganizationActions({
    onDeleteSuccess: onClose,
    organizationId,
  });

  const isDeleted = organization.deleted ?? false;

  return (
    <>
      {isDeleted ? (
        <Button
          disabled={isBusy}
          loading={isRestoring}
          onClick={handleRestore}
          size="sm"
          variant="secondary"
        >
          <RotateCcwIcon className="size-4 mr-2" />
          Restore
        </Button>
      ) : (
        <Button
          className="border-red-500 bg-red-100 text-red-600 hover:bg-red-200"
          disabled={isBusy}
          loading={isDeleting}
          onClick={() => setShowDeleteModal(true)}
          size="sm"
          variant="secondary"
        >
          <TrashIcon className="size-4 mr-2" />
          Delete
        </Button>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        itemName={`organization "${organization.name}"`}
        onConfirm={handleDelete}
        setIsOpen={setShowDeleteModal}
        showUndoWarning={false}
      />
    </>
  );
};

export const AdminOrganizationsModal: React.FC<AdminOrganizationsModalProps> = (props) => {
  const { isOpen, onClose, organizationId } = props;

  if (!isOpen) return null;

  return (
    <Modal
      centered
      footer={{
        leftButton: organizationId ? (
          <Suspense fallback={null}>
            <AdminOrganizationsModalFooterWrapper
              onClose={onClose}
              organizationId={organizationId}
            />
          </Suspense>
        ) : null,
      }}
      header={{
        title: organizationId ? (
          <Suspense fallback="Organization">
            <AdminOrganizationsModalHeaderTitle organizationId={organizationId} />
          </Suspense>
        ) : (
          'Organization'
        ),
      }}
      isOpen={isOpen}
      onClose={onClose}
      width="840px"
    >
      {organizationId ? (
        <Suspense fallback={<LoadingSkeleton />}>
          <AdminOrganizationsModalContent
            key={organizationId}
            organizationId={organizationId}
          />
        </Suspense>
      ) : (
        <div className="text-sm text-gray-500">No organization selected.</div>
      )}
    </Modal>
  );
};
